import { TFormSchema, TGridSchema } from "@/comn/hooks";
import { ptlEnvs } from "@/tra/tancis/ptli/comn";
import { api } from "@/comn";
import { comnUtils } from "@/comn/utils";

/*
 * Pre-Verification Document Management Service
 * !== 사전검증 문서관리 Service ==!
 */

/*
 * @ BASE
 * @ 서비스에서 사용하는 기본 정보 세팅
 * @ path : 화면들의 기본 웹 경로
 * @ api : 사용하는 api 의 기본 주소
 * @ api 주소가 여러개일 경우 _ 로구분하여 의미에 맞게 카멜표기업으로 표시 : api_clr
 * @ nodes : 화면의 Page.Navigation 에서 사용하는 기본 결로 Array
 */
export const BASE = {
    path: `${ptlEnvs.base}/comnmdle/doc`,
    api: `${process.env.REACT_APP_API_PTLI}/api/v1/ptli/comnmdle/doc/pre-vrfn-doc`,
    api_trgt: `${process.env.REACT_APP_API_PTLI}/api/v1/ptli/comnmdle/doc/vrfn-trgt-doc`,
    api_ctra: `${process.env.REACT_APP_API_PTLI}/api/v1/ptli/comnmdle/doc/comn-vrfn-ctra`,
    api_bulk: `${process.env.REACT_APP_API_PTLI}/api/v1/ptli/comnmdle/doc/vrfn-ctra-bulk`,
    nodes: [{ label: "Portal" }, { label: "Common Module" }, { label: "Document Management" }],
};

/*
 * @ URLS
 * @ 화면에서 사용하는 주소들 정의
 * @ 해당화면의 컴포넌트명 대문자로 정의
 */
export const URLS = {
    ptli0412001q: `${BASE.path}/ptli0412001q`, // Pre-Verification Document Management                              !== 사전검증 문서 관리 ==!
    ptli0412002s: `${BASE.path}/ptli0412002s`, // Pre-Verification Document Registration                            !== 사전검증 문서 등록 ==!
    ptli0412003s: `${BASE.path}/ptli0412003s`, // Pre-Verification Document Modification                            !== 사전검증 문서 수정 ==!

    ptli0413001q: `${BASE.path}/ptli0413001q`, // Pre-Verification Management by Document                           !== 문서별 사전검증 관리 ==!
    ptli0413002s: `${BASE.path}/ptli0413002s`, // Common Verification Criteria Registration                         !== 공통검증기준 등록 ==!
    ptli0413003s: `${BASE.path}/ptli0413003s`, // Common Verification Criteria Modification                         !== 공통검증기준 등록 ==!

    ptli0413004s: `${BASE.path}/ptli0413004s`, // Collective registration of common verification criteria items     !== 검증기준정보일괄등록 ==!
};

/*
 * @ APIS
 * @ 화면에서 사용하는 api 정의
 */
export const APIS = {
    // Retrieve the Pre-verification Document List.  !== 사전 검증 문서 목록을 조회한다. ==!
    getPreVrfnDocList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api}?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },

    // Retrieve the Pre-verification Document information.  !== 사전 검증 문서 정보를 조회한다.==!
    getPreVrfnDocInfo: (dclaDocCd: any) => api.get(`${BASE.api}/${dclaDocCd}`),

    // Register the Pre-verification Document information.  !== 사전 검증 문서 정보를 등록한다.==!
    createPreVrfnDocInfo: (info: any) => api.post(`${BASE.api}`, { ...info }),

    // Modify the pre-verification document information.  !== 사전 검증 문서 정보를 수정한다.==!
    updatePreVrfnDocInfo: (info: any) => api.put(`${BASE.api}/${info.dclaDocCd}`, { ...info }),

    // Delete the pre-verification document information.  !== 사전 검증 문서 정보를 삭제한다.==!
    deletePreVrfnDocInfo: (dclaDocCd: any) => api.delete(`${BASE.api}/${dclaDocCd}`),

    // Get a list of validated documents.  !== 검증 대상 문서 목록을 조회한다. ==!
    getVrfnTrgtDocList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api_trgt}?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },

    // Get a list of common validation criteria.  !== 공통 검증 기준 목록을 조회한다. ==!
    getComnVrfnCtraList: (data: any, page: number, size: number) => {
        return api.get(`${BASE.api_ctra}?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },

    // Get common validation criteria information.  !== 공통 검증 기준 정보를 조회한다.==!
    getComnVrfnCtraInfo: (param: any) => api.get(`${BASE.api_ctra}/${param}`),

    // Register common validation criteria information.  !== 공통 검증 기준 정보를 등록한다.==!
    createComnVrfnCtraInfo: (info: any) => api.post(`${BASE.api_ctra}`, { ...info }),

    // Modify the common validation criteria information.  !== 공통 검증 기준 정보를 수정한다.==!
    updateComnVrfnCtraInfo: (info: any) =>
        api.put(`${BASE.api_ctra}/${info.dclaDocCd}-${info.trgtTbNm}-${info.trgtColNm}`, { ...info }),

    // Delete the common validation criteria information.  !== 공통 검증 기준 정보를 삭제한다.==!
    deleteComnVrfnCtraInfo: (info: any) =>
        api.delete(`${BASE.api_ctra}/${info.dclaDocCd}-${info.trgtTbNm}-${info.trgtColNm}`),

    // Register validation criteria information in bulk.  !== 검증 기준 정보를 일괄 등록한다.==!
    insertVrfnCtraBulkList: (info: any) => api.post(`${BASE.api_bulk}`, { ...info }),
};

// Pre-Verification Document Management Search Form !== 사전 검증 문서 관리 검색 폼 ==!
export const SF_PRE_VRFN_DOC_SRCH: TFormSchema = {
    id: "form_preVrfnDocSrch",
    schema: {
        bsopClsfCd: { type: "select", label: "L_BSOP_CLSF_NM", all: true, area: "comnCd", comnCd: "COM0024" },
        dclaDocCd: { type: "text", label: "L_DCLA_DOC_CD" },
        docNm: { type: "text", label: "L_DOC_NM" },
        dclaCoTpCd: {
            type: "select",
            label: "L_CO_DCLA_TP_NM",
            all: true,
            area: "comnCd",
            comnCd: "COM0011",
        },
    },
};

// Pre-Verification Document Management List Grid Schema !== 사전 검증 문서 관리 목록 그리드 스키마 ==!
export const SG_PRE_VRFN_DOC_LIST: TGridSchema = {
    id: "grid_preVrfnDocList",
    options: { pagination: "out", edit: false, index: true },
    head: [
        { cells: [{ header: "L_BSOP_CLSF_NM", binding: "bsopClsfNm", width: "15%" }] },
        { cells: [{ header: "L_DCLA_DOC_CD", binding: "dclaDocCd", width: "20%" }] },
        { cells: [{ header: "L_DOC_NM", binding: "docNm", width: "*" }] },
        { cells: [{ header: "L_PRE_VALDN_YN", binding: "preValdnYn", width: "15%" }] },
        { cells: [{ header: "L_DOC_INTF_ID", binding: "docIntfId", width: "15%" }] },
    ],
    body: [
        { cells: [{ binding: "bsopClsfNm", edit: false, align: "left" }] },
        { cells: [{ binding: "dclaDocCd", edit: false }] },
        { cells: [{ binding: "docNm", edit: false, align: "left" }] },
        { cells: [{ binding: "preValdnYn", edit: false }] },
        { cells: [{ binding: "docIntfId", edit: false }] },
    ],
};

const codeYn = [
    { label: "Yes", value: "Y" },
    { label: "No", value: "N" },
];

// Pre-Verification Document Management Form !== 사전 검증 문서 관리 폼 ==!
export const SF_PRE_VRFN_DOC_INFO: TFormSchema = {
    id: "form_preVrfnDocInfo",
    schema: {
        bsopClsfCd: {
            type: "select",
            label: "L_BSOP_CLSF_CD",
            required: true,
            area: "comnCd",
            comnCd: "COM0024",
        },
        dclaDocCd: { type: "text", label: "L_DCLA_DOC_CD", required: true, maxLength: 12 },
        docIntfId: { type: "text", label: "L_DOC_INTF_ID", maxLength: 30 },
        docMakeUrlAddr: { type: "text", label: "L_DOC_MAKE_URL_ADDR", maxLength: 2000 },
        docNmKo: { type: "text", label: "L_DOC_NM_KO", maxLength: 300 },
        docNmEn: { type: "text", label: "L_DOC_NM_EN", maxLength: 300 },
        docNmTz: { type: "text", label: "L_DOC_NM_TZ", maxLength: 300 },
        docTpCd: { type: "select", label: "L_DOC_TP", required: true, area: "comnCd", comnCd: "COM0025" },
        preValdnYn: { type: "radio", label: "L_PRE_VALDN_YN", options: codeYn, defaultValue: "Y" },
        useYn: { type: "radio", label: "L_USE_YN", options: codeYn, defaultValue: "Y" },
        dclaCoTpCd: { type: "checkbox", label: "L_CO_DCLA_TP_NM", area: "comnCd", comnCd: "COM0011", controlSize: 10 },
        dclaCoTpCd2: { type: "code", label: "L_CO_DCLA_TP_NM", area: "comnCd", comnCd: "COM0011" },
        rmrk: { type: "textarea", label: "L_RMRK", rows: 3, maxLength: 2000 },
    },
};

// List of Documents to Verify !== 검증 대상 문서 목록 ==!
export const SG_VRFN_TRGT_DOC_LIST: TGridSchema = {
    id: "grid_vrfnTrgtDocList",
    options: { pagination: "out", edit: false, index: true },
    head: [
        { cells: [{ header: "L_BSOP_CLSF_NM", binding: "bsopClsfNm", width: "15%" }] },
        { cells: [{ header: "L_DCLA_DOC_CD", binding: "dclaDocCd", width: "20%" }] },
        { cells: [{ header: "L_DOC_NM", binding: "docNm", width: "*" }] },
        { cells: [{ header: "L_PRE_VALDN_TRGT_CNT", binding: "preValdnTrgtItm", width: "15%" }] },
    ],
    body: [
        { cells: [{ binding: "bsopClsfNm", edit: false, align: "left" }] },
        { cells: [{ binding: "dclaDocCd", edit: false }] },
        { cells: [{ binding: "docNm", edit: false, align: "left" }] },
        { cells: [{ binding: "preValdnTrgtItm", edit: false, align: "right" }] },
    ],
};

// Common Verification Criteria Search Form !== 공통 검증 기준 검색 폼 ==!
export const SF_COMN_VRFN_CTRA_SRCH: TFormSchema = {
    id: "form_comnVrfnCtra",
    schema: {
        dclaDocCd: { type: "text", label: "L_DCLA_DOC_CD" },
        docNm: { type: "text", label: "L_DOC_NM" },
    },
};

// List of Common Verification Criteria !== 공통 검증 기준 목록 ==!
export const SG_COMN_VRFN_CTRA_LIST: TGridSchema = {
    id: "grid_comnVrfnCtraList",
    options: { pagination: "out", edit: false, index: true },
    head: [
        { cells: [{ header: "L_DCLA_DOC_CD", binding: "dclaDocCd", width: "15%" }] },
        { cells: [{ header: "L_VALDN_ITM_LBL_NM", binding: "valdnItmLblNm", width: "15%" }] },
        { cells: [{ header: "L_TRGT_TB_NM", binding: "trgtTbNm", width: "29%" }] },
        { cells: [{ header: "L_TRGT_COL_NM", binding: "trgtColNm", width: "20%" }] },
        { cells: [{ header: "L_ERKY_ORD", binding: "erkyOrd", width: "10%" }] },
        { cells: [{ header: "L_COMN_CD_VALDN_YN", binding: "comnCdValdnYn", width: "15%" }] },
        { cells: [{ header: "L_INDV_CD_VALDN_YN", binding: "indvCdValdnYn", width: "15%" }] },
        { cells: [{ header: "L_RQRD_INP_VALDN_YN", binding: "rqrdInpValdnYn", width: "15%" }] },
        { cells: [{ header: "L_DT_VLDTY_VALDN_YN", binding: "dtVldtyValdnYn", width: "15%" }] },
        { cells: [{ header: "L_ITM_LEN_VALDN_YN", binding: "itmLenValdnYn", width: "15%" }] },
        { cells: [{ header: "L_NUM_VLDTY_VALDN_YN", binding: "numVldtyValdnYn", width: "15%" }] },
        { cells: [{ header: "L_NUM_DGT_VALDN_YN", binding: "numDgtValdnYn", width: "15%" }] },
    ],
    body: [
        { cells: [{ binding: "dclaDocCd", edit: false }] },
        { cells: [{ binding: "valdnItmLblNm", edit: false, align: "left" }] },
        { cells: [{ binding: "trgtTbNm", edit: false, align: "left" }] },
        { cells: [{ binding: "trgtColNm", edit: false, align: "left" }] },
        { cells: [{ binding: "erkyOrd", edit: false }] },
        { cells: [{ binding: "comnCdValdnYn", edit: false }] },
        { cells: [{ binding: "indvCdValdnYn", edit: false }] },
        { cells: [{ binding: "rqrdInpValdnYn", edit: false }] },
        { cells: [{ binding: "dtVldtyValdnYn", edit: false }] },
        { cells: [{ binding: "itmLenValdnYn", edit: false }] },
        { cells: [{ binding: "numVldtyValdnYn", edit: false }] },
        { cells: [{ binding: "numDgtValdnYn", edit: false }] },
    ],
};

// Common Verification Criteria Form !== 공통 검증 기준 폼 ==!
export const SF_COMN_VRFN_CTRA_INFO: TFormSchema = {
    id: "form_comnVrfnCtraInfo",
    schema: {
        dclaDocCd: { type: "text", label: "L_DCLA_DOC_CD", required: true, maxLength: 12, edit: false },
        docNm: { type: "text", label: "L_DOC_NM", maxLength: 300, edit: false },
        trgtTbNm: { type: "code", label: "L_TRGT_TB_NM", area: "tb", maxLength: 300, required: true },
        trgtColNm: {
            type: "code",
            label: "L_TRGT_COL_NM",
            area: "col",
            maxLength: 300,
            required: true,
            popupParams: {
                tbId: "",
            },
        },
        valdnItmLblId: { type: "code", label: "L_VALDN_ITM_LBL_ID", area: "lbl", required: true },
        itmSrtngOrd: { type: "number", label: "L_ITM_SRTNG_ORD", required: true, maxLength: 10 },
        comnCdValdnYn: { type: "radio", label: "L_COMN_CD_VALDN_YN", options: codeYn, defaultValue: "N" },
        comnCd: { type: "code", label: "L_COMN_CD", area: "comnCdMstr", maxLength: 7 },
        indvCdValdnYn: { type: "radio", label: "L_INDV_CD_VALDN_YN", options: codeYn, defaultValue: "N" },
        indvCdUprColNm: { type: "text", label: "L_INDV_CD_UPR_COL_NM", maxLength: 150 },
        indvCdTbNm: { type: "code", label: "L_INDV_CD_TB_NM", area: "indvCdTb", maxLength: 300 },
        indvCdColNm: {
            type: "code",
            label: "L_INDV_CD_COL_NM",
            area: "indvCdCol",
            maxLength: 300,
            popupParams: {
                tbId: "",
            },
        },
        indvCdAddQuryCn: { type: "textarea", label: "L_INDV_CD_ADD_QURY_CN", rows: 3, maxLength: 2000 },
        rqrdInpValdnYn: { type: "radio", label: "L_RQRD_INP_VALDN_YN", options: codeYn, defaultValue: "N" },
        amtQtyValdnYn: { type: "radio", label: "L_AMT_QTY_VALDN_YN", options: codeYn, defaultValue: "N" },
        dtVldtyValdnYn: { type: "radio", label: "L_DT_VLDTY_VALDN_YN", options: codeYn, defaultValue: "N" },
        dtVldtyTpCn: { type: "text", label: "L_DT_VLDTY_TP_CN", maxLength: 2000 },
        numVldtyValdnYn: { type: "radio", label: "L_NUM_VLDTY_VALDN_YN", options: codeYn, defaultValue: "N" },
        itmLenValdnYn: { type: "radio", label: "L_ITM_LEN_VALDN_YN", options: codeYn, defaultValue: "N" },
        itmMinLenDgt: { type: "number", label: "L_ITM_MIN_LEN_DGT", maxLength: 4 },
        itmLenDgt: { type: "number", label: "L_ITM_LEN_DGT", maxLength: 4 },
        numDgtValdnYn: { type: "radio", label: "L_NUM_DGT_VALDN_YN", options: codeYn, defaultValue: "N" },
        totDgt: { type: "number", label: "L_TOT_DGT", maxLength: 4 },
        dcmlUndrDgt: { type: "number", label: "L_DCML_UNDR_DGT", maxLength: 4 },
        erkyOrd: { type: "number", label: "L_ERKY_ORD", maxLength: 2 },
    },
};

// Bulk registration form for validation criteria information !== 검증 기준 정보 일괄 등록 폼 ==!
export const SF_VRFN_CTRA_BULK_INFO: TFormSchema = {
    id: "form_vrfnCtraBulkInfo",
    schema: {
        dclaDocCd: { type: "text", label: "L_DCLA_DOC_CD", required: true, maxLength: 12, edit: false },
    },
};

// Bulk registration list of validation criteria information !== 검증 기준 정보 일괄 등록 목록==!
export const SG_VRFN_CTRA_BULK_LIST: TGridSchema = {
    id: "grid_vrfnCtraBulkList",
    options: { pagination: false, edit: true, index: true },
    head: [
        { cells: [{ header: "L_TRGT_TB_NM", binding: "trgtTbNm", required: true, width: "15%" }] },
        { cells: [{ header: "L_TRGT_COL_NM", binding: "trgtColNm", required: true, width: "20%" }] },
        { cells: [{ header: "L_VALDN_ITM_LBL_ID", binding: "valdnItmLblId", required: true, width: "20%" }] },
        { cells: [{ header: "L_ITM_SRTNG_ORD", binding: "itmSrtngOrd", required: true, width: "15%" }] },
        { cells: [{ header: "L_COMN_CD_VALDN_YN", binding: "comnCdValdnYn", width: "20%" }] },
        { cells: [{ header: "L_COMN_CD", binding: "comnCd", width: "15%" }] },
        { cells: [{ header: "L_INDV_CD_VALDN_YN", binding: "indvCdValdnYn", width: "20%" }] },
        { cells: [{ header: "L_INDV_CD_UPR_COL_NM", binding: "indvCdUprColNm", width: "15%" }] },
        { cells: [{ header: "L_INDV_CD_TB_NM", binding: "indvCdTbNm", width: "20%" }] },
        { cells: [{ header: "L_INDV_CD_COL_NM", binding: "indvCdColNm", width: "20%" }] },
        { cells: [{ header: "L_INDV_CD_ADD_QURY_CN", binding: "indvCdAddQuryCn", width: "20%" }] },
        { cells: [{ header: "L_RQRD_INP_VALDN_YN", binding: "rqrdInpValdnYn", width: "15%" }] },
        { cells: [{ header: "L_AMT_QTY_VALDN_YN", binding: "amtQtyValdnYn", width: "20%" }] },
        { cells: [{ header: "L_DT_VLDTY_VALDN_YN", binding: "dtVldtyValdnYn", width: "15%" }] },
        { cells: [{ header: "L_DT_VLDTY_TP_CN", binding: "dtVldtyTpCn", width: "15%" }] },
        { cells: [{ header: "L_NUM_VLDTY_VALDN_YN", binding: "numVldtyValdnYn", width: "15%" }] },
        { cells: [{ header: "L_ITM_LEN_VALDN_YN", binding: "itmLenValdnYn", width: "15%" }] },
        { cells: [{ header: "L_ITM_LEN_DGT", binding: "itmLenDgt", width: "15%" }] },
        { cells: [{ header: "L_ITM_MIN_LEN_DGT", binding: "itmMinLenDgt", width: "15%" }] },
        { cells: [{ header: "L_NUM_DGT_VALDN_YN", binding: "numDgtValdnYn", width: "15%" }] },
        { cells: [{ header: "L_TOT_DGT", binding: "totDgt", width: "15%" }] },
        { cells: [{ header: "L_DCML_UNDR_DGT", binding: "dcmlUndrDgt", width: "15%" }] },
        { cells: [{ header: "L_ERKY_ORD", binding: "erkyOrd", width: "10%" }] },
    ],
    body: [
        { cells: [{ binding: "trgtTbNm", excel: "A", required: true, maxLength: 300 }] },
        { cells: [{ binding: "trgtColNm", excel: "B", required: true, maxLength: 300 }] },
        { cells: [{ binding: "valdnItmLblId", excel: "C", required: true, maxLength: 300 }] },
        { cells: [{ binding: "itmSrtngOrd", excel: "D", type: "number", required: true, maxLength: 10 }] },
        { cells: [{ binding: "comnCdValdnYn", excel: "E", type: "select", options: codeYn, defaultValue: "N" }] },
        { cells: [{ binding: "comnCd", excel: "F", maxLength: 7 }] },
        { cells: [{ binding: "indvCdValdnYn", excel: "G", type: "select", options: codeYn, defaultValue: "N" }] },
        { cells: [{ binding: "indvCdUprColNm", excel: "H", maxLength: 150 }] },
        { cells: [{ binding: "indvCdTbNm", excel: "I", maxLength: 300 }] },
        { cells: [{ binding: "indvCdColNm", excel: "J", maxLength: 300 }] },
        { cells: [{ binding: "indvCdAddQuryCn", excel: "K", maxLength: 2000 }] },
        { cells: [{ binding: "rqrdInpValdnYn", excel: "L", type: "select", options: codeYn, defaultValue: "N" }] },
        { cells: [{ binding: "amtQtyValdnYn", excel: "M", type: "select", options: codeYn, defaultValue: "N" }] },
        { cells: [{ binding: "dtVldtyValdnYn", excel: "N", type: "select", options: codeYn, defaultValue: "N" }] },
        { cells: [{ binding: "dtVldtyTpCn", excel: "O", maxLength: 2000 }] },
        { cells: [{ binding: "numVldtyValdnYn", excel: "P", type: "select", options: codeYn, defaultValue: "N" }] },
        { cells: [{ binding: "itmLenValdnYn", excel: "Q", type: "select", options: codeYn, defaultValue: "N" }] },
        { cells: [{ binding: "itmMinLenDgt", excel: "S", maxLength: 4 }] },
        { cells: [{ binding: "itmLenDgt", excel: "R", maxLength: 4 }] },
        { cells: [{ binding: "numDgtValdnYn", excel: "T", type: "select", options: codeYn, defaultValue: "N" }] },
        { cells: [{ binding: "totDgt", excel: "U", maxLength: 4 }] },
        { cells: [{ binding: "dcmlUndrDgt", excel: "V", maxLength: 4 }] },
        { cells: [{ binding: "erkyOrd", excel: "W", maxLength: 2 }] },
    ],
};
